import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export function ParserMetricsCharts() {
  const { user } = useAuth();

  const { data: chartData } = useQuery({
    queryKey: ['parser-charts', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const { data: sessions } = await supabase
        .from('bill_extraction_sessions')
        .select('*')
        .eq('user_id', user.id)
        .gte('created_at', today.toISOString());

      // Confidence Distribution
      const confidenceBuckets = {
        '0-20%': 0,
        '20-40%': 0,
        '40-60%': 0,
        '60-80%': 0,
        '80-100%': 0,
      };

      sessions?.forEach(s => {
        const conf = ((s.confidence_scores as any)?.overall_confidence || 0) * 100;
        if (conf < 20) confidenceBuckets['0-20%']++;
        else if (conf < 40) confidenceBuckets['20-40%']++;
        else if (conf < 60) confidenceBuckets['40-60%']++;
        else if (conf < 80) confidenceBuckets['60-80%']++;
        else confidenceBuckets['80-100%']++;
      });

      const confidenceData = Object.entries(confidenceBuckets).map(([range, count]) => ({
        range,
        count,
      }));

      // Parse Time Over Last 24h (by hour)
      const timeData: Record<string, { total: number; count: number }> = {};
      sessions?.forEach(s => {
        const hour = new Date(s.created_at).getHours();
        const key = `${hour}:00`;
        if (!timeData[key]) timeData[key] = { total: 0, count: 0 };
        timeData[key].total += s.processing_time_ms || 0;
        timeData[key].count++;
      });

      const parseTimeData = Object.entries(timeData).map(([time, data]) => ({
        time,
        avgSeconds: data.count > 0 ? (data.total / data.count / 1000).toFixed(2) : 0,
      }));

      // Success Rate by Provider
      const providerStats: Record<string, { success: number; total: number }> = {};
      sessions?.forEach(s => {
        const provider = s.ai_provider || 'Unknown';
        if (!providerStats[provider]) providerStats[provider] = { success: 0, total: 0 };
        providerStats[provider].total++;
        if (s.status === 'completed') providerStats[provider].success++;
      });

      const successRateData = Object.entries(providerStats).map(([provider, data]) => ({
        provider,
        successRate: data.total > 0 ? ((data.success / data.total) * 100).toFixed(1) : 0,
      }));

      return { confidenceData, parseTimeData, successRateData };
    },
    enabled: !!user?.id,
    refetchInterval: 60000,
  });

  if (!chartData) return null;

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Confidence Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData.confidenceData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="range" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Parse Time (Last 24h)</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData.parseTimeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip />
              <Line type="monotone" dataKey="avgSeconds" stroke="hsl(var(--primary))" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Success Rate by Model</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData.successRateData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="provider" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip />
              <Bar dataKey="successRate" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
